import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription } from '@/components/ui/Modal';
import { toast } from '@/hooks/useToast';
import { AppearanceSection } from '@/pages/settings/AppearanceSection';
import { FlatSection } from '@/pages/settings/FlatSection';
import { NotificationsSection } from '@/pages/settings/NotificationsSection';
import { SettingsRail } from '@/pages/settings/SettingsRail';
import { accountGroups } from '@/pages/settings/sections';
import { api } from '@/lib/api';

/**
 * Mijn account als overlay in plaats van een pagina.
 *
 * Dit zijn drie schakelaars die je midden in je werk aanraakt — thema, taal,
 * meldingen. Als eigen pagina was je daarvoor je plek kwijt: je stond in
 * Prijsadvies, ging naar instellingen, en moest daarna terugzoeken waar je
 * gebleven was. De beslisregel (Smashing/NN-g): moet de pagina eronder
 * bewaard blijven, en is de taak kort? Twee keer ja, dus overlay.
 *
 * Instellingen zelf blijft een pagina: daar zitten al vijf eigen dialogen in,
 * de secties zijn lang, en de gekozen sectie hoort in een deelbare URL.
 */
export function AccountOverlay({
  open,
  onOpenChange,
  section,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Waar de overlay op opent — een zoekresultaat wijst hier naartoe. */
  section?: string;
}) {
  const { t } = useTranslation();
  const groups = useMemo(() => accountGroups(), []);
  const ids = useMemo(() => groups.flatMap((g) => g.items.map((i) => i.id)), [groups]);
  const [current, setCurrent] = useState(ids[0] ?? '');

  // Een zoekresultaat opent op de juiste sectie; daarna bepaalt de rail het.
  useEffect(() => {
    if (open && section && ids.includes(section)) setCurrent(section);
  }, [open, section, ids]);

  const me = useQuery({ queryKey: ['auth-me'], queryFn: () => api.auth.me(), staleTime: 60_000, enabled: open });
  const tenant = useQuery({ queryKey: ['tenant-me'], queryFn: () => api.tenants.me(), enabled: open });
  const role = me.data?.role ?? 'viewer';

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="w-[min(94vw,940px)]">
        <ModalHeader>
          <ModalTitle data-meld-geheim>{me.data?.name || me.data?.email || t('account.eyebrow')}</ModalTitle>
          <ModalDescription>
            {t('account.subheading', {
              role: t(`settings.team.role.${role}`, { defaultValue: role }),
              tenant: tenant.data?.name ?? '',
            })}
          </ModalDescription>
        </ModalHeader>

        <div style={{ display: 'grid', gridTemplateColumns: '176px minmax(0,1fr)', gap: 32, alignItems: 'start' }}>
          <SettingsRail groups={groups} current={current} onSelect={setCurrent} />
          <div style={{ minWidth: 0 }}>
            {current === 'login' && <LoginSection email={me.data?.email ?? ''} />}
            {current === 'notifications' && <NotificationsSection />}
            {current === 'appearance' && <AppearanceSection />}
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}

/**
 * Inloggen. Het wachtwoord wijzigen loopt via de mail-link die er al is —
 * dezelfde weg als "wachtwoord vergeten", maar dan zonder dat je eerst moet
 * uitloggen om hem te vinden.
 */
export function LoginSection({ email }: { email: string }) {
  const { t } = useTranslation();
  const send = useMutation({
    mutationFn: () => api.auth.requestReset(email),
    onSuccess: () => toast.success(t('account.login.sent'), { description: t('account.login.sentWhere', { email }) }),
    onError: () => toast.error(t('account.login.failed')),
  });

  return (
    <FlatSection id="login" title={t('account.login.heading')} note={email}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div className="text-text-primary" style={{ fontSize: 14 }}>{t('account.login.passwordTitle')}</div>
          <p className="text-text-muted" style={{ fontSize: 12.5, lineHeight: 1.55, margin: '3px 0 0', maxWidth: '60ch' }}>
            {t('account.login.passwordBody')}
          </p>
        </div>
        <Button variant="secondary" size="sm" disabled={!email || send.isPending} onClick={() => send.mutate()}>
          {t('account.login.change')}
        </Button>
      </div>
    </FlatSection>
  );
}
